import { memo, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { getUserInitials, generateUserGradient } from '@/lib/avatar-utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'

interface EnhancedAvatarProps {
    name: string
    avatarUrl?: string | null
    avatarColor?: string | null
    userId?: number | string
    size?: 'sm' | 'md' | 'lg' | 'xl'
    className?: string
}

const sizeClasses = {
    sm: 'h-8 w-8 text-xs',
    md: 'h-10 w-10 text-sm',
    lg: 'h-12 w-12 text-base',
    xl: 'h-16 w-16 text-lg',
}

export const EnhancedAvatar = memo(function EnhancedAvatar({
    name,
    avatarUrl,
    avatarColor,
    userId,
    size = 'md',
    className,
}: EnhancedAvatarProps) {
    const initials = useMemo(() => getUserInitials(name), [name])
    const gradient = useMemo(() =>
        avatarColor || (userId ? generateUserGradient(userId, name) : 'from-gray-500 to-gray-600'),
        [avatarColor, userId, name]
    )

    return (
        <Avatar className={cn(sizeClasses[size], className)}>
            <AvatarImage
                src={avatarUrl || ''}
                alt={`${name}'s avatar`}
                className="object-cover"
            />
            <AvatarFallback
                className={cn(
                    'bg-gradient-to-br font-semibold text-white',
                    gradient
                )}
            >
                {initials}
            </AvatarFallback>
        </Avatar>
    )
})
